import { ToggleButton, ToggleButtonGroup, styled } from '@mui/material';
import React from 'react';

import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';

export type BenchmarkSegmentedToggleOption<TValue extends string> = {
  value: TValue;
  label: string; // already translated
};

type BenchmarkSegmentedToggleProps<TValue extends string> = {
  value: TValue;
  options: BenchmarkSegmentedToggleOption<TValue>[];
  onChange: (value: TValue) => void;
  ariaLabel?: string;
};

const Group = styled(ToggleButtonGroup, { shouldForwardProp: (prop) => prop !== 'chartPalette' })<{
  chartPalette: ChartPalette;
}>(({ theme, chartPalette }) => ({
  padding: theme.spacing(1),
  gap: theme.spacing(1),
  borderRadius: 12,
  border: `1px solid ${chartPalette.borders.subtle}`,
  backgroundColor: chartPalette.backgrounds.sunken,

  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid transparent',
    borderRadius: 8,
    textTransform: 'none',
    paddingInline: theme.spacing(4),
    paddingBlock: theme.spacing(2),
    fontSize: 14,
    fontWeight: 600,
    color: chartPalette.text.secondary,

    '&:not(:first-of-type), &:not(:last-of-type)': {
      borderRadius: 8,
    },
    '&:hover': {
      backgroundColor: chartPalette.backgrounds.raised,
      color: chartPalette.text.primary,
    },
    '&.Mui-selected': {
      backgroundColor: chartPalette.backgrounds.surface,
      borderColor: chartPalette.borders.strong,
      color: chartPalette.text.primary,
      boxShadow: chartPalette.shadow,
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${chartPalette.series.categorical[0]}`,
      outlineOffset: 2,
    },
  },
}));

// Exclusive toggle that cannot be emptied: clicking the selected option keeps it selected.
const BenchmarkSegmentedToggle = <TValue extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: BenchmarkSegmentedToggleProps<TValue>) => {
  const palette = useChartPalette();

  return (
    <Group
      exclusive
      chartPalette={palette}
      value={value}
      aria-label={ariaLabel}
      onChange={(_event, next: TValue | null) => {
        if (next !== null) onChange(next);
      }}>
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </Group>
  );
};

export default BenchmarkSegmentedToggle;
